// =============================================================================
// SimSense Platform — Support Ticket Page [OT-223H]
// =============================================================================

import { useState, useEffect, useRef } from "react";
import { api } from "../utils/api";
import { useToast } from "../components/Toast";
import { parseTicketBody, TicketAttachments } from "../components/TicketAttachments";

const statusConfig: Record<string, { label: string; color: string; bg: string }> = {
  open: { label: "Open", color: "#fbbf24", bg: "rgba(251, 191, 36, 0.15)" },
  in_progress: { label: "In Progress", color: "#60a5fa", bg: "rgba(96, 165, 250, 0.15)" },
  closed: { label: "Closed", color: "#4ade80", bg: "rgba(74, 222, 128, 0.15)" },
};

export function Support() {
  const [tickets, setTickets] = useState<any[]>([]);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [screenshots, setScreenshots] = useState<{ url: string; name: string }[]>([]);
  const [uploadingScreenshot, setUploadingScreenshot] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [closingId, setClosingId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const screenshotInputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();

  useEffect(() => {
    document.title = "Support | SimSense";
    async function fetchTickets() {
      try {
        const data = await api<any[]>("/api/support/my-tickets");
        setTickets(data || []);
        // OT-223G: Mark tickets as viewed to clear bell notification
        await api("/api/support/mark-viewed", { method: "POST" }).catch(() => {});
      } catch (err) {
        console.error("Failed to fetch tickets", err);
      } finally {
        setLoading(false);
      }
    }
    fetchTickets();
  }, []);

  const handleScreenshotUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (screenshots.length >= 3) {
      toast.error("Maximum 3 screenshots per ticket.");
      return;
    }
    setUploadingScreenshot(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch("/api/support/upload-screenshot", {
        method: "POST",
        body: formData,
        credentials: "include",
      });
      if (!res.ok) throw new Error("Upload failed");
      const data = await res.json();
      setScreenshots(prev => [...prev, { url: data.url, name: file.name }]);
    } catch {
      toast.error("Failed to upload screenshot.");
    } finally {
      setUploadingScreenshot(false);
      if (screenshotInputRef.current) screenshotInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!subject || !body) return;

    // Append screenshot URLs to ticket body
    let fullBody = body;
    if (screenshots.length > 0) {
      fullBody += "\n\n--- Screenshots ---\n" + screenshots.map(s => s.url).join("\n");
    }

    setSubmitting(true);
    try {
      const newTicket = await api<any>("/api/support/tickets", {
        method: "POST",
        body: JSON.stringify({ subject, body: fullBody })
      });
      setTickets([newTicket, ...tickets]);
      setSubject("");
      setBody("");
      setScreenshots([]);
      toast.success("Support ticket submitted!");
    } catch (err) {
      toast.error("Failed to submit ticket.");
    } finally {
      setSubmitting(false);
    }
  };

  const closeTicket = async (ticketId: string) => {
    setClosingId(ticketId);
    try {
      const updated = await api<any>(`/api/support/tickets/${ticketId}/close`, { method: "POST" });
      setTickets(tickets.map(t => t.id === ticketId ? { ...t, ...updated } : t));
      toast.success("Ticket closed.");
    } catch (err) {
      toast.error("Failed to close ticket.");
    } finally {
      setClosingId(null);
    }
  };

  const handleNewMessage = (ticketId: string, message: any) => {
    setTickets(tickets.map(t => {
      if (t.id !== ticketId) return t;
      const msgs = t.messages || [];
      return { ...t, messages: [...msgs, message], status: t.status === "closed" ? "open" : t.status };
    }));
  };

  return (
    <div style={{ maxWidth: "800px", margin: "0 auto" }}>
      <div style={{ marginBottom: "32px" }}>
        <h2 style={{ fontSize: "28px", marginBottom: "8px" }}>Support & Help</h2>
        <p style={{ color: "var(--text2)" }}>Need help with the platform or have a feature request? Submit a ticket below.</p>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "32px", alignItems: "start" }}>
        {/* Submit Ticket Form */}
        <section style={{ background: "var(--card)", padding: "24px", borderRadius: "12px", border: "1px solid var(--border)" }}>
          <h3 style={{ marginBottom: "20px", fontSize: "18px" }}>New Support Ticket</h3>
          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <div>
              <label style={{ display: "block", fontSize: "12px", marginBottom: "6px", color: "var(--text2)", fontWeight: 600 }}>Subject</label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Brief summary of the issue"
                style={{ width: "100%", padding: "10px", background: "var(--bg)", border: "1px solid var(--border)", color: "#fff", borderRadius: "6px", fontSize: "14px" }}
                required
              />
            </div>
            <div>
              <label style={{ display: "block", fontSize: "12px", marginBottom: "6px", color: "var(--text2)", fontWeight: 600 }}>Description</label>
              <textarea
                rows={6}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Details about what's happening. If it's a telemetry issue, please include the session date/track."
                style={{ width: "100%", padding: "10px", background: "var(--bg)", border: "1px solid var(--border)", color: "#fff", borderRadius: "6px", resize: "vertical", fontSize: "14px", fontFamily: "inherit" }}
                required
              />
            </div>
            <div>
              <label style={{ display: "block", fontSize: "12px", marginBottom: "6px", color: "var(--text2)", fontWeight: 600 }}>Screenshots (optional)</label>
              <input
                ref={screenshotInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                onChange={handleScreenshotUpload}
                style={{ display: "none" }}
              />
              <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: screenshots.length > 0 ? "8px" : "0" }}>
                {screenshots.map((s, i) => (
                  <div key={i} style={{
                    display: "flex", alignItems: "center", gap: "6px",
                    padding: "4px 10px", background: "rgba(59,130,246,0.1)",
                    border: "1px solid rgba(59,130,246,0.25)", borderRadius: "6px", fontSize: "12px"
                  }}>
                    <span style={{ color: "var(--text1)", maxWidth: "120px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.name}</span>
                    <button
                      type="button"
                      onClick={() => setScreenshots(prev => prev.filter((_, j) => j !== i))}
                      style={{ background: "none", border: "none", color: "#ef4444", cursor: "pointer", fontSize: "14px", padding: "0 2px" }}
                    >
                      &times;
                    </button>
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={() => screenshotInputRef.current?.click()}
                disabled={uploadingScreenshot || screenshots.length >= 3}
                style={{
                  padding: "8px 14px", background: "none",
                  border: "1px dashed var(--border)", color: "var(--text2)",
                  borderRadius: "6px", fontSize: "12px", cursor: "pointer",
                  opacity: screenshots.length >= 3 ? 0.5 : 1,
                }}
              >
                {uploadingScreenshot ? "Uploading..." : screenshots.length >= 3 ? "Max 3 screenshots" : "+ Attach Screenshot"}
              </button>
            </div>
            <button
              type="submit"
              disabled={submitting}
              style={{ padding: "12px", background: "var(--accent)", color: "#fff", border: "none", borderRadius: "6px", fontWeight: 600, cursor: submitting ? "wait" : "pointer", fontSize: "14px" }}
            >
              {submitting ? "Submitting..." : "Submit Ticket"}
            </button>
          </form>
        </section>

        {/* Previous Tickets */}
        <section>
          <h3 style={{ marginBottom: "20px", fontSize: "18px" }}>Your Tickets</h3>
          {loading ? (
            <div style={{ color: "var(--text3)" }}>Loading tickets...</div>
          ) : tickets.length === 0 ? (
            <div style={{ color: "var(--text3)", fontSize: "14px", padding: "40px", border: "1px dashed var(--border)", borderRadius: "12px", textAlign: "center" }}>
              No tickets submitted yet.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              {tickets.map(t => (
                <TicketCard
                  key={t.id}
                  ticket={t}
                  expanded={expandedId === t.id}
                  onToggle={() => setExpandedId(expandedId === t.id ? null : t.id)}
                  onClose={() => closeTicket(t.id)}
                  closing={closingId === t.id}
                  onNewMessage={(msg) => handleNewMessage(t.id, msg)}
                />
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function TicketCard({ ticket: t, expanded, onToggle, onClose, closing, onNewMessage }: {
  ticket: any;
  expanded: boolean;
  onToggle: () => void;
  onClose: () => void;
  closing: boolean;
  onNewMessage: (msg: any) => void;
}) {
  const sc = statusConfig[t.status] || statusConfig.open;
  const messages: any[] = t.messages || [];
  const hasMessages = messages.length > 0;
  const hasAdminReply = messages.some((m: any) => m.senderType === "admin") || t.adminNotes;

  return (
    <div style={{
      background: "var(--card)", borderRadius: "8px",
      border: `1px solid ${expanded ? "var(--accent-dim, var(--border))" : "var(--border)"}`,
      transition: "border-color 0.15s"
    }}>
      {/* Header — clickable to expand */}
      <div
        onClick={onToggle}
        style={{ padding: "16px", cursor: "pointer", userSelect: "none" }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px", alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span style={{
              fontSize: "9px", fontWeight: 800, textTransform: "uppercase",
              color: sc.color, background: sc.bg,
              padding: "2px 8px", borderRadius: "4px", letterSpacing: "0.5px"
            }}>
              {sc.label}
            </span>
            {hasAdminReply && !expanded && (
              <span style={{ fontSize: "9px", color: "#60a5fa", fontWeight: 700 }}>
                Admin replied
              </span>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span style={{ fontSize: "11px", color: "var(--text3)" }}>
              {new Date(t.createdAt).toLocaleDateString()}
            </span>
            <span style={{ fontSize: "10px", color: "var(--text3)", transform: expanded ? "rotate(180deg)" : "none", transition: "transform 0.15s" }}>
              ▼
            </span>
          </div>
        </div>
        <div style={{ fontWeight: 600, fontSize: "14px", color: "#fff" }}>{t.subject}</div>
        {!expanded && (
          <div style={{ fontSize: "12px", color: "var(--text2)", marginTop: "4px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {parseTicketBody(t.body).text}
          </div>
        )}
      </div>

      {/* Expanded: full body + conversation thread */}
      {expanded && (
        <div style={{ borderTop: "1px solid var(--border)" }}>
          {/* Original ticket body + attachments */}
          {(() => {
            const { text, screenshots } = parseTicketBody(t.body);
            return (
              <div style={{ padding: "16px", fontSize: "13px", color: "var(--text2)", lineHeight: "1.6" }}>
                <div style={{ whiteSpace: "pre-wrap" }}>{text}</div>
                <TicketAttachments urls={screenshots} />
              </div>
            );
          })()}

          {/* Legacy admin notes (if no messages exist yet) */}
          {t.adminNotes && !hasMessages && (
            <div style={{ margin: "0 16px 16px", padding: "10px", background: "rgba(59,130,246,0.08)", borderRadius: "6px", border: "1px solid rgba(59,130,246,0.2)" }}>
              <div style={{ fontSize: "9px", color: "#60a5fa", fontWeight: 800, textTransform: "uppercase", marginBottom: "4px", letterSpacing: "0.5px" }}>
                Admin Response
              </div>
              <div style={{ fontSize: "12px", color: "var(--text1)", whiteSpace: "pre-wrap", lineHeight: 1.5 }}>
                {t.adminNotes}
              </div>
            </div>
          )}

          {/* Conversation thread */}
          {hasMessages && (
            <div style={{ padding: "0 16px 8px" }}>
              <div style={{ fontSize: "10px", color: "var(--text3)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "8px" }}>
                Conversation
              </div>
              {messages.map((msg: any) => (
                <MessageBubble key={msg.id} message={msg} />
              ))}
            </div>
          )}

          {/* Reply input + close button */}
          <div style={{ padding: "12px 16px", borderTop: "1px solid var(--border)", display: "flex", gap: "8px", alignItems: "center" }}>
            {t.status !== "closed" ? (
              <>
                <ReplyInput ticketId={t.id} onSent={onNewMessage} />
                <button
                  onClick={(e) => { e.stopPropagation(); onClose(); }}
                  disabled={closing}
                  style={{
                    padding: "8px 12px", fontSize: "11px", fontWeight: 600,
                    background: "none", border: "1px solid var(--border)",
                    color: "var(--text2)", borderRadius: "6px", cursor: "pointer",
                    whiteSpace: "nowrap", flexShrink: 0
                  }}
                >
                  {closing ? "..." : "Close Ticket"}
                </button>
              </>
            ) : (
              <div style={{ fontSize: "12px", color: "var(--text3)", fontStyle: "italic", flex: 1, textAlign: "center", padding: "4px" }}>
                This ticket is closed. Send a message to reopen.
              </div>
            )}
            {t.status === "closed" && <ReplyInput ticketId={t.id} onSent={onNewMessage} placeholder="Reply to reopen..." />}
          </div>
        </div>
      )}
    </div>
  );
}

function MessageBubble({ message: m }: { message: any }) {
  const isAdmin = m.senderType === "admin";
  // OT-304: Parse attachments from message body using same separator as tickets
  const { text, screenshots } = parseTicketBody(m.body || "");
  return (
    <div style={{
      display: "flex",
      justifyContent: isAdmin ? "flex-start" : "flex-end",
      marginBottom: "8px"
    }}>
      <div style={{
        maxWidth: "85%",
        padding: "8px 12px",
        borderRadius: isAdmin ? "2px 10px 10px 10px" : "10px 2px 10px 10px",
        background: isAdmin ? "rgba(59,130,246,0.12)" : "rgba(255,255,255,0.06)",
        border: `1px solid ${isAdmin ? "rgba(59,130,246,0.25)" : "var(--border)"}`,
      }}>
        <div style={{ fontSize: "9px", fontWeight: 700, color: isAdmin ? "#60a5fa" : "var(--text3)", marginBottom: "3px", textTransform: "uppercase", letterSpacing: "0.3px" }}>
          {isAdmin ? (m.sender?.displayName || "Admin") : "You"}
          <span style={{ fontWeight: 400, marginLeft: "8px", letterSpacing: 0, textTransform: "none" }}>
            {new Date(m.createdAt).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
          </span>
        </div>
        <div style={{ fontSize: "13px", color: "var(--text1)", whiteSpace: "pre-wrap", lineHeight: 1.5 }}>
          {text}
        </div>
        {screenshots.length > 0 && <TicketAttachments urls={screenshots} />}
      </div>
    </div>
  );
}

function ReplyInput({ ticketId, onSent, placeholder, apiPrefix = "/api/support" }: {
  ticketId: string;
  onSent: (msg: any) => void;
  placeholder?: string;
  apiPrefix?: string;
}) {
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [attachments, setAttachments] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFileAttach = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return;
    if (file.size > 5 * 1024 * 1024) return;
    if (attachments.length >= 3) return;

    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch("/api/support/upload-screenshot", { method: "POST", body: fd, credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        if (data.url) setAttachments(prev => [...prev, data.url]);
      }
    } catch { /* silent */ }
    finally {
      setUploading(false);
      if (e.target) e.target.value = "";
    }
  };

  const send = async () => {
    if ((!reply.trim() && attachments.length === 0) || sending) return;
    setSending(true);
    try {
      // OT-304: Append screenshot URLs to message body using same separator pattern as tickets
      let body = reply.trim();
      if (attachments.length > 0) {
        body += `\n\n--- Screenshots ---\n${attachments.join("\n")}`;
      }
      const msg = await api<any>(`${apiPrefix}/tickets/${ticketId}/messages`, {
        method: "POST",
        body: JSON.stringify({ body })
      });
      onSent(msg);
      setReply("");
      setAttachments([]);
    } catch {
      // silent fail
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "6px", flex: 1 }}>
      {/* Attachment previews */}
      {attachments.length > 0 && (
        <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
          {attachments.map((url, i) => (
            <div key={i} style={{ position: "relative" }}>
              <img src={url} alt="" style={{ width: 48, height: 48, borderRadius: "4px", objectFit: "cover", border: "1px solid var(--border)" }} />
              <button
                onClick={() => setAttachments(prev => prev.filter((_, j) => j !== i))}
                style={{
                  position: "absolute", top: -4, right: -4,
                  width: 16, height: 16, borderRadius: "50%",
                  background: "#ef4444", color: "#fff", border: "none",
                  fontSize: "10px", cursor: "pointer", display: "flex",
                  alignItems: "center", justifyContent: "center", lineHeight: 1,
                }}
              >&times;</button>
            </div>
          ))}
        </div>
      )}
      <div style={{ display: "flex", gap: "6px" }}>
        <input
          ref={inputRef}
          type="text"
          value={reply}
          onChange={e => setReply(e.target.value)}
          onKeyDown={e => e.key === "Enter" && send()}
          placeholder={placeholder || "Type a reply..."}
          style={{
            flex: 1, padding: "8px 12px", background: "var(--bg)",
            border: "1px solid var(--border)", color: "#fff",
            borderRadius: "6px", fontSize: "13px", outline: "none"
          }}
        />
        {/* OT-304: Attach screenshot button */}
        <button
          onClick={() => fileRef.current?.click()}
          disabled={uploading || attachments.length >= 3}
          title="Attach screenshot"
          style={{
            padding: "8px", background: "rgba(255,255,255,0.05)",
            border: "1px solid var(--border)", borderRadius: "6px",
            color: "var(--text2)", cursor: uploading ? "wait" : "pointer",
            display: "flex", alignItems: "center",
            opacity: attachments.length >= 3 ? 0.3 : 1,
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <polyline points="21 15 16 10 5 21" />
          </svg>
        </button>
        <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleFileAttach} />
        <button
          onClick={send}
          disabled={sending || (!reply.trim() && attachments.length === 0)}
          style={{
            padding: "8px 14px", background: "var(--accent)", color: "#fff",
            border: "none", borderRadius: "6px", fontWeight: 600,
            fontSize: "12px", cursor: sending ? "wait" : "pointer",
            opacity: (!reply.trim() && attachments.length === 0) ? 0.5 : 1
          }}
        >
          {sending ? "..." : "Send"}
        </button>
      </div>
    </div>
  );
}
